(() => {
  if (window.sePhotoGalleryBooted) return;
  window.sePhotoGalleryBooted = true;
  const controllers = new WeakMap();

  const createGallery = (section) => {
    const track = section.querySelector('[data-se-photo-track]');
    const cards = Array.from(section.querySelectorAll('[data-se-photo-card]'));
    const controls = section.querySelector('[data-se-photo-controls]');
    const previous = section.querySelector('[data-se-photo-previous]');
    const next = section.querySelector('[data-se-photo-next]');
    const progress = section.querySelector('[data-se-photo-progress]');
    const count = section.querySelector('[data-se-photo-count]');
    const status = section.querySelector('[data-se-photo-status]');
    if (!track || !cards.length || !controls || !previous || !next || !progress || !count || !status) return null;

    let frame = 0;
    let announcementTimer = 0;
    let targetResetTimer = 0;
    let pendingTarget = null;
    let resizeObserver;
    const isRtl = () => getComputedStyle(track).direction === 'rtl';
    const format = (first, last) => {
      const template = first === last ? section.dataset.sePhotoSingle : section.dataset.sePhotoRange;
      return template.replaceAll('__CURRENT__', String(first)).replaceAll('__FIRST__', String(first)).replaceAll('__LAST__', String(last)).replaceAll('__TOTAL__', String(cards.length));
    };

    const update = (announce = true) => {
      const viewport = track.getBoundingClientRect();
      const bounds = cards.map((card) => card.getBoundingClientRect());
      const rtl = isRtl();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maxScroll > 1;
      const atStart = rtl ? bounds[0].right <= viewport.right + 1 : bounds[0].left >= viewport.left - 1;
      const atEnd = rtl ? bounds[bounds.length - 1].left >= viewport.left - 1 : bounds[bounds.length - 1].right <= viewport.right + 1;
      controls.hidden = !hasOverflow;
      previous.disabled = !hasOverflow || atStart;
      next.disabled = !hasOverflow || atEnd;
      let visible = bounds.map((rect, index) => ({ rect, index })).filter(({ rect }) => rect.left >= viewport.left - 1 && rect.right <= viewport.right + 1);
      if (!visible.length) visible = bounds.map((rect, index) => ({ rect, index })).filter(({ rect }) => rect.right > viewport.left + 1 && rect.left < viewport.right - 1);
      const first = visible.length ? visible[0].index + 1 : 1;
      const last = visible.length ? visible[visible.length - 1].index + 1 : first;
      const position = format(first, last);
      count.textContent = position;
      const distance = rtl ? bounds[0].right - viewport.right : viewport.left - bounds[0].left;
      progress.value = hasOverflow ? Math.min(100, Math.max(0, distance / maxScroll * 100)) : 100;
      clearTimeout(announcementTimer);
      if (announce) announcementTimer = window.setTimeout(() => {
        if (status.textContent !== position) status.textContent = position;
      }, 180);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    const navigate = (direction) => {
      const viewport = track.getBoundingClientRect();
      const first = cards[0].getBoundingClientRect();
      const rtl = isRtl();
      const current = rtl ? first.right - viewport.right : viewport.left - first.left;
      const step = cards.length > 1 ? Math.abs(cards[1].getBoundingClientRect().left - first.left) : first.width;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      pendingTarget = Math.min(maxScroll, Math.max(0, (pendingTarget === null ? current : pendingTarget) + direction * step));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: rtl ? -pendingTarget : pendingTarget, behavior: reducedMotion ? 'auto' : 'smooth' });
      clearTimeout(targetResetTimer);
      targetResetTimer = window.setTimeout(() => { pendingTarget = null; }, 600);
      scheduleUpdate();
    };

    const goPrevious = () => navigate(-1);
    const goNext = () => navigate(1);
    const onKeydown = (event) => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      navigate(isRtl() ? -direction : direction);
    };
    const onDirectScroll = () => {
      pendingTarget = null;
      clearTimeout(targetResetTimer);
    };
    const onResize = () => {
      onDirectScroll();
      scheduleUpdate();
    };
    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    track.addEventListener('keydown', onKeydown);
    track.addEventListener('pointerdown', onDirectScroll, { passive: true });
    track.addEventListener('wheel', onDirectScroll, { passive: true });
    previous.addEventListener('click', goPrevious);
    next.addEventListener('click', goNext);
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', onResize);
    }
    update(false);
    return {
      destroy() {
        cancelAnimationFrame(frame);
        clearTimeout(announcementTimer);
        clearTimeout(targetResetTimer);
        if (resizeObserver) resizeObserver.disconnect();
        window.removeEventListener('resize', onResize);
        track.removeEventListener('scroll', scheduleUpdate);
        track.removeEventListener('keydown', onKeydown);
        track.removeEventListener('pointerdown', onDirectScroll);
        track.removeEventListener('wheel', onDirectScroll);
        previous.removeEventListener('click', goPrevious);
        next.removeEventListener('click', goNext);
      }
    };
  };

  const galleriesIn = (scope) => {
    const galleries = Array.from(scope.querySelectorAll('[data-se-photo-gallery]'));
    if (scope.matches && scope.matches('[data-se-photo-gallery]')) galleries.unshift(scope);
    return galleries;
  };
  const initialize = (scope = document) => {
    galleriesIn(scope).forEach((section) => {
      if (controllers.has(section)) return;
      const controller = createGallery(section);
      if (controller) controllers.set(section, controller);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    galleriesIn(event.target).forEach((section) => {
      const controller = controllers.get(section);
      if (controller) controller.destroy();
      controllers.delete(section);
    });
  });
})();
